import { useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Navigate, Link } from 'react-router-dom';
import { Shield, Eye, EyeOff, Mail, Lock, User, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import SEO from '@/components/SEO';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { validatePassword, getPasswordErrorMessage } from '@/lib/passwordValidation';

// EU Countries with calling codes
const countries = [
  { code: '+43', country: 'Österreich', flag: '🇦🇹', search: 'osterreich austria' },
  { code: '+32', country: 'Belgien', flag: '🇧🇪', search: 'belgien belgium' },
  { code: '+359', country: 'Bulgarien', flag: '🇧🇬', search: 'bulgarien bulgaria' },
  { code: '+385', country: 'Kroatien', flag: '🇭🇷', search: 'kroatien croatia' },
  { code: '+357', country: 'Zypern', flag: '🇨🇾', search: 'zypern cyprus' },
  { code: '+420', country: 'Tschechien', flag: '🇨🇿', search: 'tschechien czech' },
  { code: '+45', country: 'Dänemark', flag: '🇩🇰', search: 'danemark denmark' },
  { code: '+372', country: 'Estland', flag: '🇪🇪', search: 'estland estonia' },
  { code: '+358', country: 'Finnland', flag: '🇫🇮', search: 'finnland finland' },
  { code: '+33', country: 'Frankreich', flag: '🇫🇷', search: 'frankreich france' },
  { code: '+49', country: 'Deutschland', flag: '🇩🇪', search: 'deutschland germany' },
  { code: '+30', country: 'Griechenland', flag: '🇬🇷', search: 'griechenland greece' },
  { code: '+36', country: 'Ungarn', flag: '🇭🇺', search: 'ungarn hungary' },
  { code: '+353', country: 'Irland', flag: '🇮🇪', search: 'irland ireland' },
  { code: '+39', country: 'Italien', flag: '🇮🇹', search: 'italien italy' },
  { code: '+371', country: 'Lettland', flag: '🇱🇻', search: 'lettland latvia' },
  { code: '+370', country: 'Litauen', flag: '🇱🇹', search: 'litauen lithuania' },
  { code: '+352', country: 'Luxemburg', flag: '🇱🇺', search: 'luxemburg luxembourg' },
  { code: '+356', country: 'Malta', flag: '🇲🇹', search: 'malta' },
  { code: '+31', country: 'Niederlande', flag: '🇳🇱', search: 'niederlande netherlands holland' },
  { code: '+48', country: 'Polen', flag: '🇵🇱', search: 'polen poland' },
  { code: '+351', country: 'Portugal', flag: '🇵🇹', search: 'portugal' },
  { code: '+40', country: 'Rumänien', flag: '🇷🇴', search: 'rumanien romania' },
  { code: '+421', country: 'Slowakei', flag: '🇸🇰', search: 'slowakei slovakia' },
  { code: '+386', country: 'Slowenien', flag: '🇸🇮', search: 'slowenien slovenia' },
  { code: '+34', country: 'Spanien', flag: '🇪🇸', search: 'spanien spain' },
  { code: '+46', country: 'Schweden', flag: '🇸🇪', search: 'schweden sweden' },
  { code: '+41', country: 'Schweiz', flag: '🇨🇭', search: 'schweiz switzerland' },
];

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    gender: '',
    password: '',
    confirmPassword: ''
  });
  const [countryCode, setCountryCode] = useState('+49');
  const [openCountrySelect, setOpenCountrySelect] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  const { signUp, user } = useAuth();
  const { toast } = useToast();

  // Redirect if already authenticated
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const validateForm = () => {
    if (!formData.firstName.trim()) {
      return 'Bitte geben Sie Ihren Vornamen ein.';
    }
    if (!formData.lastName.trim()) {
      return 'Bitte geben Sie Ihren Nachnamen ein.';
    }
    if (!formData.email.trim()) {
      return 'Bitte geben Sie Ihre E-Mail-Adresse ein.';
    }
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      return 'Bitte geben Sie eine gültige E-Mail-Adresse ein.';
    }
    if (!formData.phone.trim()) {
      return 'Bitte geben Sie Ihre Telefonnummer ein.';
    }
    if (!/^\d+$/.test(formData.phone)) {
      return 'Telefonnummer darf nur Ziffern enthalten.';
    }
    if (formData.phone.length < 5 || formData.phone.length > 15) {
      return 'Telefonnummer muss zwischen 5 und 15 Ziffern lang sein.';
    }
    if (!formData.gender) {
      return 'Bitte wählen Sie Ihr Geschlecht aus.';
    }
    
    // Passwort-Validierung
    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      const errorMsg = getPasswordErrorMessage(passwordValidation);
      return errorMsg || 'Das Passwort erfüllt nicht die Anforderungen.';
    }
    if (formData.password !== formData.confirmPassword) {
      return 'Die Passwörter stimmen nicht überein.';
    }
    if (!acceptTerms) {
      return 'Sie müssen den Allgemeinen Geschäftsbedingungen (AGB) zustimmen, um sich zu registrieren.';
    }
    if (!acceptPrivacy) {
      return 'Sie müssen der Datenschutzerklärung zustimmen, um sich zu registrieren.';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    const { error } = await signUp(formData.email, formData.password, {
      first_name: formData.firstName,
      last_name: formData.lastName,
      phone: `${countryCode}${formData.phone}`,
      gender: formData.gender
    });

    if (error) {
      console.error('Registrierungsfehler:', error);
      
      // Handle specific error cases
      if (error.message.includes('User already registered')) {
        setError('Diese E-Mail-Adresse ist bereits registriert. Bitte melden Sie sich an oder verwenden Sie eine andere E-Mail-Adresse.');
      } else if (error.message.includes('Invalid email')) {
        setError('Die eingegebene E-Mail-Adresse ist ungültig.');
      } else if (error.message.includes('Password')) {
        setError('Das Passwort erfüllt nicht die Anforderungen. Bitte verwenden Sie ein stärkeres Passwort.');
      } else if (error.message.includes('rate limit')) {
        setError('Zu viele Registrierungsversuche. Bitte warten Sie einen Moment und versuchen Sie es erneut.');
      } else {
        // Show the actual error message for better debugging
        setError(`Fehler bei der Registrierung: ${error.message || 'Ein unbekannter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.'}`);
      }
      setLoading(false);
    } else {
      // Send welcome email
      try {
        const { supabase } = await import('@/integrations/supabase/client');
        await supabase.functions.invoke('send-registration-welcome', {
          body: {
            email: formData.email,
            first_name: formData.firstName,
            last_name: formData.lastName,
            gender: formData.gender,
            language: 'de'
          }
        });
        console.log('Welcome email sent successfully');
      } catch (emailError) {
        console.error('Failed to send welcome email:', emailError);
        // Don't show error to user, just log it
      }

      setSuccess(true);
      toast({
        title: "Registrierung erfolgreich",
        description: "Bitte prüfen Sie Ihre E-Mails zur Bestätigung Ihres Kontos."
      });
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center px-4">
        <Card className="w-full max-w-md shadow-soft">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 gradient-primary rounded-full flex items-center justify-center">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl text-secondary">Registrierung erfolgreich!</CardTitle>
            <CardDescription className="text-lg">
              Wir haben Ihnen eine Bestätigungs-E-Mail gesendet. Bitte prüfen Sie Ihr Postfach und klicken Sie auf den Bestätigungslink.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-muted-foreground">
              Können Sie die E-Mail nicht finden? Prüfen Sie auch Ihren Spam-Ordner.
            </p>
            <div className="space-y-2">
              <Button asChild variant="hero" className="w-full">
                <Link to="/login">Zur Anmeldung</Link>
              </Button>
              <Button asChild variant="outline" className="w-full">
                <Link to="/">Zur Startseite</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle flex items-center justify-center px-4 py-8">
      <SEO
        title="Konto erstellen | OnlineSicher24"
        description="Erstellen Sie Ihr kostenloses OnlineSicher24-Konto und sichern Sie sich vor Online-Betrug."
        canonicalPath="/register"
      />
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Shield className="h-12 w-12 text-primary" />
            <h1 className="text-3xl font-bold">OnlineSicher24</h1>
          </div>
          <p className="text-muted-foreground text-lg">
            Registrieren Sie sich für maximalen Schutz
          </p>
        </div>

        <Card className="shadow-soft">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Konto erstellen</CardTitle>
            <CardDescription>
              Geben Sie Ihre Daten ein, um ein sicheres Konto zu erstellen
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="gender" className="text-base font-medium">
                  Geschlecht *
                </Label>
                <select
                  id="gender"
                  value={formData.gender}
                  onChange={(e) => setFormData(prev => ({ ...prev, gender: e.target.value }))}
                  className="flex h-11 w-full items-center rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                >
                  <option value="" disabled>Bitte wählen</option>
                  <option value="male">Mann</option>
                  <option value="female">Frau</option>
                  <option value="diverse">Divers</option>
                  <option value="prefer_not_to_say">Keine Angabe</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName" className="text-base font-medium">
                    Vorname *
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input
                      id="firstName"
                      name="firstName"
                      type="text"
                      placeholder="Max"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="pl-10 h-11"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName" className="text-base font-medium">
                    Nachname *
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input
                      id="lastName"
                      name="lastName"
                      type="text"
                      placeholder="Mustermann"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="pl-10 h-11"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-base font-medium">
                  E-Mail-Adresse *
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="max.mustermann@email.de"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="pl-10 h-11"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-base font-medium">
                  Telefonnummer *
                </Label>
                <div className="flex gap-2">
                  <Popover open={openCountrySelect} onOpenChange={setOpenCountrySelect}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={openCountrySelect}
                        className="w-[140px] h-11 justify-between"
                      >
                        {countries.find((country) => country.code === countryCode)?.flag}{' '}
                        {countryCode}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[280px] p-0">
                      <Command>
                        <CommandInput placeholder="Land suchen..." />
                        <CommandList>
                          <CommandEmpty>Kein Land gefunden.</CommandEmpty>
                          <CommandGroup>
                            {countries.map((country) => (
                              <CommandItem
                                key={country.code}
                                value={`${country.search} ${country.code}`}
                                onSelect={() => {
                                  setCountryCode(country.code);
                                  setOpenCountrySelect(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    countryCode === country.code ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                {country.flag} {country.country} ({country.code})
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <div className="relative flex-1">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="1234567890"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="pl-10 h-11"
                      required
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Beispiel: Wählen Sie +49 und geben Sie 1234567890 ein
                </p>
              </div>


              <div className="space-y-2">
                <Label htmlFor="password" className="text-base font-medium">
                  Passwort *
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Mindestens 8 Zeichen"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="pl-10 pr-12 h-11"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {formData.password && (
                  <div className="text-xs space-y-1 mt-2">
                    <p className={validatePassword(formData.password).minLength ? 'text-green-600' : 'text-muted-foreground'}>
                      ✓ Mindestens 8 Zeichen
                    </p>
                    <p className={validatePassword(formData.password).hasUpper ? 'text-green-600' : 'text-muted-foreground'}>
                      ✓ Mindestens ein Großbuchstabe
                    </p>
                    <p className={validatePassword(formData.password).hasNumber ? 'text-green-600' : 'text-muted-foreground'}>
                      ✓ Mindestens eine Zahl
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-base font-medium">
                  Passwort bestätigen *
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Passwort wiederholen"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className="pl-10 pr-12 h-11"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-4 pt-4">

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="terms"
                    checked={acceptTerms}
                    onCheckedChange={(checked) => setAcceptTerms(checked === true)}
                    className="mt-0.5"
                  />
                  <label htmlFor="terms" className="text-sm leading-5 cursor-pointer">
                    Ich habe die{' '}
                    <Link to="/agb" className="text-primary hover:text-primary/80 underline font-medium" target="_blank">
                      Allgemeinen Geschäftsbedingungen (AGB)
                    </Link>{' '}
                    gelesen und akzeptiere sie ausdrücklich. *
                  </label>
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="privacy"
                    checked={acceptPrivacy}
                    onCheckedChange={(checked) => setAcceptPrivacy(checked === true)}
                    className="mt-0.5"
                  />
                  <label htmlFor="privacy" className="text-sm leading-5 cursor-pointer">
                    Ich habe die{' '}
                    <Link to="/datenschutz" className="text-primary hover:text-primary/80 underline font-medium" target="_blank">
                      Datenschutzerklärung
                    </Link>{' '}
                    gelesen und akzeptiere die Verarbeitung meiner Daten gemäß dieser Erklärung. *
                  </label>
                </div>

                <p className="text-xs text-muted-foreground mt-2">
                  * Diese Felder sind verpflichtend. Die Zustimmung zu den AGB ist für die Kontoeröffnung erforderlich.
                </p>
              </div>

              <Button
                type="submit"
                className="w-full h-11"
                variant="hero"
                disabled={loading}
              >
                {loading ? 'Registrierung läuft...' : 'Konto erstellen'}
              </Button>

              <div className="text-center pt-4 border-t">
                <p className="text-muted-foreground">
                  Bereits ein Konto?{' '}
                  <Link
                    to="/login"
                    className="text-primary hover:text-primary/80 font-medium"
                  >
                    Jetzt anmelden
                  </Link>
                </p>
              </div>
            </form>
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <Link
            to="/"
            className="text-muted-foreground hover:text-foreground"
          >
            ← Zurück zur Startseite
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;